import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faHome, faBook, faSearch } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";

import { API_URL_ENDPOINT } from "../appsetting";
import { useApi } from "../helpers/useApi";
import { BookCard } from "../components/BookCard";
import {
  confirmDeleteToast,
  sendError,
  sendSuccess,
} from "../helpers/Helper";
import { Routess } from "../Routes/routes";
import { theme } from "../constants/theme";

export const CategoryBooks = () => {
  const callApi = useApi();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [data, setData] = useState({
    items: [],
    pageNo: 1,
    pageSize: 12,
    totalPage: 0,
    totalRecords: 0,
  });

  useEffect(() => {
    getData(1);
  }, []);


  useEffect(() => {
    const delay = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        getData(1);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  const getData = async (cp = null, append = false) => {
    try {
      setDataLoading(true);
      const filters = [];
      if (searchTerm) filters.push(`SearchText:${searchTerm}`);
      filters.push(`ShowDraft:true`);
      filters.push(`CategoryID:${id}`);

      const queryParams = [
        filters.length ? `filters=${filters.join(";")}` : null,
        `pageNo=${cp ?? data.pageNo}`,
        `pageSize=${data.pageSize}`,
        `sortOrder=desc`,
      ]
        .filter(Boolean)
        .join("&");

      const response = await callApi(API_URL_ENDPOINT.Books + `?${queryParams}`);

      if (response.status) {
        if (append) {
          setData((prev) => ({
            ...response.data,
            items: [...prev.items, ...response.data.items],
          }));
        } else {
          setData(response.data);
        }
      }
    } catch (error) {
      sendError("Failed to fetch books");
      setData({
        items: [],
        pageNo: 1,
        pageSize: 10,
        totalPage: 0,
        totalRecords: 0,
      });
    } finally {
      setDataLoading(false);
    }
  };

  const handleView = (id) => {
    navigate(Routess.Books.path + `/${id}`);
  };

  const handleDelete = (book) => {
    confirmDeleteToast(async () => {
      try {
        const response = await callApi(API_URL_ENDPOINT.Books + '/' + book.id, { method: "DELETE" });
        if (response.status) {
          sendSuccess(response.message);
          getData(1);
        } else {
          sendError(response.message);
        }
      } catch (error) {
        sendError(error.message);
      }
    });
  };

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-lightGray">
        <div className="mb-3 md:mb-0">
          <div className="flex items-center mt-0">
            <button
              className="w-10 h-10 rounded-full border border-lightGray flex items-center justify-center mr-3 hover:bg-light hover:text-primary transition-colors text-gray"
              onClick={() => navigate(-1)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h4 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-primary to-accent mb-0">
                Category Books
              </h4>
              <small className="text-gray text-xs block">Browse books in this category</small>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              placeholder="Search books..."
              className="pl-10 pr-4 py-2 rounded-lg border border-lightGray bg-white focus:ring-2 focus:ring-accent/20 focus:border-accent outline-none w-full md:w-64 transition-all"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsTyping(true);
              }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-lightGray overflow-hidden mb-6">
        <div className="p-6">
          {/* Stats Row */}
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-lightGray">
            <span className="text-sm text-gray">
              Showing <strong className="text-primary">{data.items.length}</strong> of{' '}
              <strong className="text-secondary">{data.totalRecords}</strong> books
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6">
            {dataLoading && data.items.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
                <p className="text-gray">Loading books...</p>
              </div>
            ) : data?.items?.length > 0 ? (
              data.items.map((item, index) => (
                <BookCard key={index} book={item} onBookClick={() => handleView(item.id)} onDelete={handleDelete} />
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <FontAwesomeIcon
                  icon={faBook}
                  className="text-5xl text-gray-300 mb-4"
                />
                <p className="text-lg text-gray">No books found in this category</p>
              </div>
            )}
          </div>

          {/* Load More Button */}
          {data?.pageNo < data?.totalPage && (
            <div className="text-center mt-8 pt-4">
              <button
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-orange-600 text-white font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={dataLoading}
                onClick={() => getData(data.pageNo + 1, true)}
              >
                {dataLoading ? (
                  <>Loading...</>
                ) : (
                  "Load More Books"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
